import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Settings, Key, Mail, Scissors, Eye, EyeOff, Save, CheckCircle, Database, RefreshCw, Users, Package, Loader2, AlertCircle, Clock, Plus, Trash2, GripVertical, Bell, X } from "lucide-react";

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    cutting_cost_matrix: [],
    openai_api_key: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "RIMCORE PRECUT",
    smtp_use_tls: true,
    notification_emails: [],
    job_changing_time_minutes: 30,
    per_sqmm_cutting_time_seconds: 0.0001,
    working_schedule: [
      { day: 1, label: "Monday", start: "09:00", end: "18:00", enabled: true },
      { day: 2, label: "Tuesday", start: "09:00", end: "18:00", enabled: true },
      { day: 3, label: "Wednesday", start: "09:00", end: "18:00", enabled: true },
      { day: 4, label: "Thursday", start: "09:00", end: "18:00", enabled: true },
      { day: 5, label: "Friday", start: "09:00", end: "18:00", enabled: true },
      { day: 6, label: "Saturday", start: "09:00", end: "14:00", enabled: true },
      { day: 7, label: "Sunday", start: "00:00", end: "00:00", enabled: false },
    ],
    delivery_buffer_days: 1,
  });

  // ERP Sync state
  const [customerSyncing, setCustomerSyncing] = useState(false);
  const [productSyncing, setProductSyncing] = useState(false);
  const [customerSyncResult, setCustomerSyncResult] = useState(null);
  const [productSyncResult, setProductSyncResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const DEFAULT_SCHEDULE = [
    { day: 1, label: "Monday", start: "09:00", end: "18:00", enabled: true },
    { day: 2, label: "Tuesday", start: "09:00", end: "18:00", enabled: true },
    { day: 3, label: "Wednesday", start: "09:00", end: "18:00", enabled: true },
    { day: 4, label: "Thursday", start: "09:00", end: "18:00", enabled: true },
    { day: 5, label: "Friday", start: "09:00", end: "18:00", enabled: true },
    { day: 6, label: "Saturday", start: "09:00", end: "14:00", enabled: true },
    { day: 7, label: "Sunday", start: "00:00", end: "00:00", enabled: false },
  ];

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/global");
      const data = res.data;
      // Transform working_schedule from API to UI format
      if (data.working_schedule && Array.isArray(data.working_schedule)) {
        const schedMap = {};
        data.working_schedule.forEach(s => { schedMap[s.day] = s; });
        data.working_schedule = DEFAULT_SCHEDULE.map(d => ({
          ...d,
          start: schedMap[d.day]?.start || d.start,
          end: schedMap[d.day]?.end || d.end,
          enabled: d.day in schedMap ? true : (schedMap[d.day] !== undefined),
        }));
        // Mark days in schedule as enabled
        const enabledDays = new Set(res.data.working_schedule.map(s => s.day));
        data.working_schedule = DEFAULT_SCHEDULE.map(d => ({
          ...d,
          start: schedMap[d.day]?.start || d.start,
          end: schedMap[d.day]?.end || d.end,
          enabled: enabledDays.has(d.day),
        }));
      }
      setSettings(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      let payload = {};
      if (section === "cutting_matrix") {
        payload = { cutting_cost_matrix: settings.cutting_cost_matrix };
      } else if (section === "openai") {
        payload = { openai_api_key: settings.openai_api_key };
      } else if (section === "notifications") {
        payload = { notification_emails: settings.notification_emails || [] };
      } else if (section === "smtp") {
        payload = {
          smtp_host: settings.smtp_host,
          smtp_port: settings.smtp_port,
          smtp_username: settings.smtp_username,
          smtp_password: settings.smtp_password,
          smtp_from_email: settings.smtp_from_email,
          smtp_from_name: settings.smtp_from_name,
          smtp_use_tls: settings.smtp_use_tls,
        };
      } else if (section === "production") {
        const schedule = (settings.working_schedule || [])
          .filter(d => d.enabled)
          .map(d => ({ day: d.day, start: d.start, end: d.end }));
        payload = {
          job_changing_time_minutes: settings.job_changing_time_minutes,
          per_sqmm_cutting_time_seconds: settings.per_sqmm_cutting_time_seconds,
          working_schedule: schedule,
          delivery_buffer_days: settings.delivery_buffer_days,
        };
      }
      await api.put("/settings/global", payload);
      const labels = { cutting_matrix: "Cutting Cost Matrix", openai: "OpenAI", smtp: "SMTP", notifications: "Notification Emails", production: "Production Schedule" };
      toast.success(`${labels[section] || section} settings saved`);
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addMatrixRow = () => {
    setSettings(prev => ({
      ...prev,
      cutting_cost_matrix: [...(prev.cutting_cost_matrix || []), { thickness: "", moq: 1, rate: 0 }],
    }));
  };

  const removeMatrixRow = (idx) => {
    setSettings(prev => ({
      ...prev,
      cutting_cost_matrix: (prev.cutting_cost_matrix || []).filter((_, i) => i !== idx),
    }));
  };

  const updateMatrixRow = (idx, field, value) => {
    setSettings(prev => {
      const matrix = [...(prev.cutting_cost_matrix || [])];
      matrix[idx] = { ...matrix[idx], [field]: value };
      return { ...prev, cutting_cost_matrix: matrix };
    });
  };

  const [newNotifEmail, setNewNotifEmail] = useState("");

  const addNotifEmail = () => {
    const email = newNotifEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if ((settings.notification_emails || []).includes(email)) return;
    setSettings(prev => ({ ...prev, notification_emails: [...(prev.notification_emails || []), email] }));
    setNewNotifEmail("");
  };

  const removeNotifEmail = (email) => {
    setSettings(prev => ({ ...prev, notification_emails: (prev.notification_emails || []).filter(e => e !== email) }));
  };

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    setSettings(prev => {
      const matrix = [...(prev.cutting_cost_matrix || [])];
      const [moved] = matrix.splice(dragIdx, 1);
      matrix.splice(idx, 0, moved);
      return { ...prev, cutting_cost_matrix: matrix };
    });
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const pollSyncStatus = useCallback((taskId, type) => {
    const setSyncing = type === "customers" ? setCustomerSyncing : setProductSyncing;
    const setResult = type === "customers" ? setCustomerSyncResult : setProductSyncResult;

    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/erp/sync-status/${taskId}`);
        if (res.data.status !== "running") {
          clearInterval(pollRef.current);
          setSyncing(false);
          setResult(res.data);
          if (res.data.status === "completed") {
            toast.success(`${type === "customers" ? "Customer" : "Product"} sync completed!`);
          } else {
            toast.error(`Sync failed: ${res.data.error || "Unknown error"}`);
          }
        }
      } catch {
        clearInterval(pollRef.current);
        setSyncing(false);
      }
    }, 3000);
  }, [api]);

  const handleERPSync = async (type) => {
    const setSyncing = type === "customers" ? setCustomerSyncing : setProductSyncing;
    const setResult = type === "customers" ? setCustomerSyncResult : setProductSyncResult;
    setResult(null);
    setSyncing(true);
    try {
      const res = await api.post(`/erp/load-${type}`);
      toast.info(`${type === "customers" ? "Customer" : "Product"} sync started in background...`);
      pollSyncStatus(res.data.task_id, type);
    } catch (error) {
      setSyncing(false);
      toast.error(error.response?.data?.detail || `Failed to start ${type} sync`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-2">
        <Settings className="w-6 h-6 text-slate-400" />
        <h1 className="font-heading text-2xl uppercase tracking-wider">Admin Settings</h1>
      </div>

      {/* Section 1: Cutting Cost Matrix */}
      <Card className="card-industrial" data-testid="cutting-cost-matrix-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-orange-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">Cutting Cost Matrix</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
            Define cutting rates by material thickness and minimum order quantity (MOQ). During costing, the system picks the row where the product's thickness matches and the order quantity meets the MOQ — using the highest qualifying MOQ tier. Leave thickness blank to apply a rate to any thickness.
          </div>

          {/* Table header */}
          <div className="border border-slate-200">
            <div className="grid grid-cols-[24px_1fr_1fr_1fr_44px] gap-2 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
              <span></span>
              <span>Thickness (mm)</span>
              <span>Min. Qty (MOQ)</span>
              <span>Rate (₹ / pcs)</span>
              <span></span>
            </div>

            {(settings.cutting_cost_matrix || []).length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-slate-400">
                No entries yet. Add a row to define cutting costs.
              </div>
            )}

            {(settings.cutting_cost_matrix || []).map((row, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={() => handleDrop(idx)}
                onDragEnd={handleDragEnd}
                className={`grid grid-cols-[24px_1fr_1fr_1fr_44px] gap-2 px-3 py-2 border-t items-center transition-colors ${
                  dragOverIdx === idx && dragIdx !== idx
                    ? "bg-orange-50 border-orange-300"
                    : dragIdx === idx
                    ? "opacity-40 bg-slate-50"
                    : "border-slate-100"
                }`}
                data-testid={`matrix-row-${idx}`}
              >
                <span
                  className="flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                >
                  <GripVertical className="w-4 h-4" />
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={row.thickness ?? ""}
                  onChange={(e) => updateMatrixRow(idx, "thickness", e.target.value === "" ? null : parseFloat(e.target.value))}
                  className="rounded-none font-mono h-8 text-sm"
                  placeholder="Any"
                  data-testid={`matrix-thickness-${idx}`}
                />
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={row.moq ?? 1}
                  onChange={(e) => updateMatrixRow(idx, "moq", parseInt(e.target.value) || 1)}
                  className="rounded-none font-mono h-8 text-sm"
                  data-testid={`matrix-moq-${idx}`}
                />
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={row.rate ?? 0}
                  onChange={(e) => updateMatrixRow(idx, "rate", parseFloat(e.target.value) || 0)}
                  className="rounded-none font-mono h-8 text-sm"
                  data-testid={`matrix-rate-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeMatrixRow(idx)}
                  className="flex items-center justify-center w-8 h-8 text-slate-400 hover:text-red-600 hover:bg-red-50 border border-slate-200"
                  data-testid={`matrix-delete-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              onClick={addMatrixRow}
              variant="outline"
              className="h-9 rounded-none border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="add-matrix-row-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Row
            </Button>
            <Button
              onClick={() => handleSave("cutting_matrix")}
              disabled={saving}
              className="h-9 rounded-none bg-orange-600 hover:bg-orange-700"
              data-testid="save-cutting-matrix-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Matrix
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: OpenAI / ChatGPT API Key */}
      <Card className="card-industrial" data-testid="openai-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-emerald-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">AI Nesting Optimization (OpenAI)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="bg-slate-50 border border-slate-200 p-3 mb-4 text-sm text-slate-600">
            When configured, the system uses ChatGPT to optimize shape arrangement on material, reducing wastage beyond the standard algorithm. If no key is set, the algorithmic nesting is used automatically.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider">OpenAI API Key</Label>
              <div className="relative mt-1">
                <Input
                  type={showApiKey ? "text" : "password"}
                  value={settings.openai_api_key || ""}
                  onChange={(e) => updateField("openai_api_key", e.target.value)}
                  className="rounded-none font-mono pr-10"
                  placeholder="sk-..."
                  data-testid="openai-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Get your key from platform.openai.com</p>
            </div>
            <Button
              onClick={() => handleSave("openai")}
              disabled={saving}
              className="h-10 rounded-none bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-openai-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save API Key
            </Button>
          </div>
          {settings.openai_api_key && (
            <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              <span>API Key configured — AI nesting optimization is active</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: SMTP / Email Configuration */}
      <Card className="card-industrial" data-testid="smtp-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">Email Configuration (SMTP)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
            Configure SMTP for all system emails: customer activation, order notifications, quotation approvals, and dispatch alerts. For Gmail, use smtp.gmail.com with an App Password.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">SMTP Host *</Label>
              <Input
                value={settings.smtp_host || ""}
                onChange={(e) => updateField("smtp_host", e.target.value)}
                className="rounded-none font-mono mt-1"
                placeholder="smtp.gmail.com"
                data-testid="smtp-host-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">SMTP Port *</Label>
              <Input
                type="number"
                value={settings.smtp_port || 587}
                onChange={(e) => updateField("smtp_port", parseInt(e.target.value) || 587)}
                className="rounded-none font-mono mt-1"
                data-testid="smtp-port-input"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer h-10">
                <input
                  type="checkbox"
                  checked={settings.smtp_use_tls !== false}
                  onChange={(e) => updateField("smtp_use_tls", e.target.checked)}
                  className="w-4 h-4"
                  data-testid="smtp-tls-checkbox"
                />
                <span className="text-sm">Use TLS</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Username / Email *</Label>
              <Input
                value={settings.smtp_username || ""}
                onChange={(e) => updateField("smtp_username", e.target.value)}
                className="rounded-none font-mono mt-1"
                placeholder="your-email@gmail.com"
                data-testid="smtp-username-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Password / App Password *</Label>
              <div className="relative mt-1">
                <Input
                  type={showSmtpPass ? "text" : "password"}
                  value={settings.smtp_password || ""}
                  onChange={(e) => updateField("smtp_password", e.target.value)}
                  className="rounded-none font-mono pr-10"
                  placeholder="App password"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPass(!showSmtpPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSmtpPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">From Email</Label>
              <Input
                value={settings.smtp_from_email || ""}
                onChange={(e) => updateField("smtp_from_email", e.target.value)}
                className="rounded-none font-mono mt-1"
                placeholder="noreply@rimcore.com (defaults to username)"
                data-testid="smtp-from-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">From Name</Label>
              <Input
                value={settings.smtp_from_name || ""}
                onChange={(e) => updateField("smtp_from_name", e.target.value)}
                className="rounded-none font-mono mt-1"
                placeholder="RIMCORE PRECUT"
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => handleSave("smtp")}
              disabled={saving}
              className="h-10 rounded-none bg-blue-700 hover:bg-blue-800"
              data-testid="save-smtp-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Email Settings
            </Button>
          </div>
          {settings.smtp_host && settings.smtp_username && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <CheckCircle className="w-4 h-4" />
              <span>SMTP configured — Customer activation emails can be sent</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3b: Notification Emails */}
      <Card className="card-industrial" data-testid="notification-emails-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">Order Notification Emails</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
            These email addresses receive an alert whenever a new order is placed by a customer. Add your sales / operations team IDs here.
          </div>

          <div className="space-y-2">
            {(settings.notification_emails || []).length === 0 && (
              <div className="py-3 text-sm text-slate-400 text-center border border-dashed border-slate-200">
                No notification emails configured.
              </div>
            )}
            {(settings.notification_emails || []).map((email) => (
              <div key={email} className="flex items-center justify-between px-3 py-2 border border-slate-200 bg-slate-50">
                <span className="text-sm font-mono text-slate-700">{email}</span>
                <button
                  type="button"
                  onClick={() => removeNotifEmail(email)}
                  className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              type="email"
              value={newNotifEmail}
              onChange={(e) => setNewNotifEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNotifEmail())}
              className="rounded-none font-mono flex-1"
              placeholder="team@company.com"
              data-testid="notif-email-input"
            />
            <Button
              type="button"
              onClick={addNotifEmail}
              variant="outline"
              className="h-10 rounded-none border-slate-300"
              data-testid="add-notif-email-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => handleSave("notifications")}
              disabled={saving}
              className="h-10 rounded-none bg-indigo-700 hover:bg-indigo-800"
              data-testid="save-notif-emails-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Notification Emails
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Production Schedule */}
      <Card className="card-industrial" data-testid="production-schedule-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">Production Schedule & Delivery Estimation</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
            Configure production parameters to auto-calculate estimated delivery dates. Each day can have different working hours (e.g. shorter Saturday).
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Job Changing Time (minutes)</Label>
              <Input
                type="number"
                step="1"
                value={settings.job_changing_time_minutes ?? 30}
                onChange={(e) => updateField("job_changing_time_minutes", parseFloat(e.target.value) || 0)}
                className="rounded-none font-mono mt-1"
                data-testid="job-change-time-input"
              />
              <p className="text-xs text-slate-400 mt-1">Time to setup/change between each item</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Per Sq.MM Cutting Time (seconds)</Label>
              <Input
                type="number"
                step="0.00001"
                value={settings.per_sqmm_cutting_time_seconds ?? 0.0001}
                onChange={(e) => updateField("per_sqmm_cutting_time_seconds", parseFloat(e.target.value) || 0)}
                className="rounded-none font-mono mt-1"
                data-testid="per-sqmm-time-input"
              />
              <p className="text-xs text-slate-400 mt-1">Cutting time per square millimeter</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Buffer / Transit Days</Label>
              <Input
                type="number"
                step="1"
                value={settings.delivery_buffer_days ?? 1}
                onChange={(e) => updateField("delivery_buffer_days", parseInt(e.target.value) || 0)}
                className="rounded-none font-mono mt-1"
                data-testid="buffer-days-input"
              />
              <p className="text-xs text-slate-400 mt-1">Extra working days added for transit</p>
            </div>
          </div>

          {/* Per-Day Working Hours */}
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Working Hours (Per Day)</Label>
            <div className="border border-slate-200">
              <div className="grid grid-cols-[120px_1fr_1fr_60px] gap-2 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                <span>Day</span><span>Start Time</span><span>End Time</span><span>Active</span>
              </div>
              {(settings.working_schedule || DEFAULT_SCHEDULE).map((dayEntry, idx) => (
                <div key={dayEntry.day} className={`grid grid-cols-[120px_1fr_1fr_60px] gap-2 px-3 py-2 items-center border-t border-slate-100 ${!dayEntry.enabled ? "opacity-40" : ""}`}>
                  <span className="text-sm font-medium">{dayEntry.label}</span>
                  <Input
                    type="time"
                    value={dayEntry.start}
                    disabled={!dayEntry.enabled}
                    onChange={(e) => {
                      const schedule = [...(settings.working_schedule || DEFAULT_SCHEDULE)];
                      schedule[idx] = { ...schedule[idx], start: e.target.value };
                      updateField("working_schedule", schedule);
                    }}
                    className="rounded-none font-mono h-8 text-sm"
                    data-testid={`day-${dayEntry.day}-start`}
                  />
                  <Input
                    type="time"
                    value={dayEntry.end}
                    disabled={!dayEntry.enabled}
                    onChange={(e) => {
                      const schedule = [...(settings.working_schedule || DEFAULT_SCHEDULE)];
                      schedule[idx] = { ...schedule[idx], end: e.target.value };
                      updateField("working_schedule", schedule);
                    }}
                    className="rounded-none font-mono h-8 text-sm"
                    data-testid={`day-${dayEntry.day}-end`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const schedule = [...(settings.working_schedule || DEFAULT_SCHEDULE)];
                      schedule[idx] = { ...schedule[idx], enabled: !schedule[idx].enabled };
                      updateField("working_schedule", schedule);
                    }}
                    className={`w-8 h-8 border text-xs font-mono ${dayEntry.enabled ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-300 border-slate-200"}`}
                    data-testid={`day-${dayEntry.day}-toggle`}
                  >
                    {dayEntry.enabled ? "ON" : "—"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => handleSave("production")}
              disabled={saving}
              className="h-10 rounded-none bg-amber-700 hover:bg-amber-800"
              data-testid="save-production-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Production Schedule
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: ERP Database Sync */}
      <Card className="card-industrial" data-testid="erp-sync-section">
        <CardHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-violet-500" />
            <CardTitle className="font-heading text-base uppercase tracking-wider">ERP Database Sync</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
            Manually sync Customer and Product master data from your external ERP database. Existing records are updated; new ones are inserted.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Sync */}
            <div className="border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-slate-600" />
                <span className="font-heading text-sm uppercase tracking-wider">Customer Sync</span>
              </div>
              <p className="text-xs text-slate-500">Syncs from API_CUSTOMER table. New customers are created as inactive.</p>
              <Button
                onClick={() => handleERPSync("customers")}
                disabled={customerSyncing}
                className="w-full h-10 rounded-none bg-violet-700 hover:bg-violet-800"
                data-testid="sync-customers-btn"
              >
                {customerSyncing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Load Customers from ERP</>
                )}
              </Button>
              {customerSyncResult && (
                <div className={`text-xs p-2 ${customerSyncResult.status === "completed" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="customer-sync-result">
                  {customerSyncResult.status === "completed" ? (
                    <div className="flex items-start gap-1">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div>
                        Synced {customerSyncResult.result?.total_in_erp || 0} from ERP
                        (New: {customerSyncResult.result?.new_customers || 0} |
                        Updated: {customerSyncResult.result?.updated_customers || 0})
                        <br/>Total in DB: {customerSyncResult.result?.total_in_db || 0}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Failed: {customerSyncResult.error}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Product Sync */}
            <div className="border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-slate-600" />
                <span className="font-heading text-sm uppercase tracking-wider">Product & Rate Sync</span>
              </div>
              <p className="text-xs text-slate-500">Syncs from allcomp_Price_WITH_Disc_Pull. Includes customer-specific rates.</p>
              <Button
                onClick={() => handleERPSync("products")}
                disabled={productSyncing}
                className="w-full h-10 rounded-none bg-violet-700 hover:bg-violet-800"
                data-testid="sync-products-btn"
              >
                {productSyncing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Load Products from ERP</>
                )}
              </Button>
              {productSyncResult && (
                <div className={`text-xs p-2 ${productSyncResult.status === "completed" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="product-sync-result">
                  {productSyncResult.status === "completed" ? (
                    <div className="flex items-start gap-1">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div>
                        Products: Updated {productSyncResult.result?.updated_products || 0} |
                        Skipped {productSyncResult.result?.skipped_products || 0} |
                        In DB: {productSyncResult.result?.total_products_in_db || 0}
                        <br/>
                        Rates: New {productSyncResult.result?.new_customer_rates || 0} |
                        Updated {productSyncResult.result?.updated_customer_rates || 0} |
                        In DB: {productSyncResult.result?.total_rates_in_db || 0}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Failed: {productSyncResult.error}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
